import React, { useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Typography,
  Chip,
  Avatar,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/CategoryRounded";
import { Link } from "react-router-dom";
import ICategory from "../../entities/ICategory";
import CategoryService from "../../services/CategoryService";
import { getErrorMessage, resolveImageUrl } from "../../services/apiUtils";
import { toPlainText } from "../../services/textUtils";
import { theme } from "../../theme";

type CategoryActionsProps = {
  category: ICategory;
  parentName?: string;
  onDeleted?: () => void;
};

const CategoryActions: React.FC<CategoryActionsProps> = ({ category, parentName, onDeleted }) => {
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleDelete = () => {
    setDeleting(true);
    CategoryService.deleteCategory(category.categoryId)
      .then(() => {
        setSnackbar({ open: true, message: "Category deleted.", severity: "success" });
        setConfirmOpen(false);
        onDeleted?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not delete this category."),
          severity: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  const description = toPlainText(category.description);

  return (
    <Box>
      <Tooltip title="View Category details">
        <IconButton onClick={() => setViewOpen(true)} sx={{ color: theme.palette.secondary.main }}>
          <PreviewIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit Category details">
        <Link to={`/AddCategory/${category.categoryId}`}>
          <IconButton sx={{ color: theme.palette.primary.main }}>
            <EditIcon />
          </IconButton>
        </Link>
      </Tooltip>
      <Tooltip title="Delete Category">
        <IconButton onClick={() => setConfirmOpen(true)} sx={{ color: theme.status.danger }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FDB44D 100%)`,
            color: "#fff",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            variant="rounded"
            src={resolveImageUrl(category.thumbnail) || undefined}
            sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}
          >
            <CategoryIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {category.title || "Untitled Category"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Category ID: {category.categoryId}
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {!!category.parentId && (
            <Box mb={2.5}>
              <Chip
                label={`Parent Category: ${parentName || `#${category.parentId}`}`}
                sx={{ bgcolor: theme.palette.primary.light, color: theme.palette.primary.main, fontWeight: 600 }}
              />
            </Box>
          )}
          <Typography variant="overline" color="text.secondary">Description</Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {description || "No description provided."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setViewOpen(false)} variant="outlined">Close</Button>
          <Button
            component={Link}
            to={`/AddCategory/${category.categoryId}`}
            variant="contained"
            startIcon={<EditIcon />}
          >
            Edit Category
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{category.title}"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryActions;
